import React from 'react';
import { IConfirmationResponse, IGetConfirmationResponse } from '@lib/types';
import { CLUB_PHONE_NUMBERS, TransactionStatus, ProductType, ProductSubType } from '@ace-clublabs/payment-types';
import { createAccountOnClick, loginOnClick } from '../utils/urlHelper';
import { Confirmation as PCLConfirmation } from '@ace-clublabs/payments-component-library';
import { Typography, Link } from '@ace-clublabs/shared-component-library';

type Props = IGetConfirmationResponse & {
  isMobileApp: boolean;
  minimalPage: boolean;
};

const Confirmation: React.FC<Props> = (props) => {
  const {
    primaryEmail,
    secondaryEmail,
    paymentType,
    brand,
    last4,
    amountPaid,
    confirmationNumber,
    membershipNumber,
    routingNumber,
    autoPay,
    clubCode,
  } = props as IConfirmationResponse;
  const { minimalPage } = props as Props;
  const phoneNumber = CLUB_PHONE_NUMBERS[clubCode];
  const autoPayFailed = autoPay?.status && autoPay?.status !== TransactionStatus.Succeeded;
  const autoPaySucceeded = autoPay?.status && autoPay?.status === TransactionStatus.Succeeded;
  const isStandaloneAutoPay = !amountPaid && autoPay?.status === TransactionStatus.Succeeded;

  const handleLoginClick = () => loginOnClick(clubCode);
  const handleCreateAccountClick = createAccountOnClick;

  const headerText = isStandaloneAutoPay
    ? 'Thank you for enrolling in Auto Pay'
    : amountPaid && autoPaySucceeded
    ? 'Thank you for your payment and Auto Pay enrollment'
    : 'Thank you for your payment';

  let confirmationProps = {
    title: headerText,
    confirmationDetails: {
      confirmationNumber,
      productId: membershipNumber,
      paymentDetails: {
        autoPayStatus: autoPaySucceeded ? TransactionStatus.Succeeded : undefined,
        paymentStatus: amountPaid > 0 ? TransactionStatus.Succeeded : undefined,
        amountPaid,
        paymentType,
        card: brand ? { brand, last4 } : undefined,
        echeck: routingNumber ? { last4 } : undefined,
      },
      emails: { primaryEmail, secondaryEmail },
    },
    button: {
      primary: { buttonText: 'Log in', onClick: handleLoginClick },
    },
    displayScheduledPayment: false,
    errorMessage: autoPayFailed ? (
      <>
        Sorry, we encountered an issue and could not complete your Auto Pay enrollment.{' '}
        <Link variant="b2Regular" target="_self" href={`${window.location.origin}/payments/manage-autopay`}>
          Manage Auto Pay
        </Link>
        .
      </>
    ) : undefined,
    autoPayMessage: autoPaySucceeded ? (
      <Typography variant="b2Regular">
        <strong>Auto Pay: Enrolled</strong> — Starting with your next billing cycle, we'll begin automatically charging
        or debiting your selected payment method.
      </Typography>
    ) : undefined,
    phoneNumber,
    productType: ProductType.Membership,
    discontinueAutoPayMessage: autoPaySucceeded ? true : false,
  };

  return (
    <div className="confirmation-container" id="confirmation-container">
      <PCLConfirmation
        {...confirmationProps}
        minimalPage={minimalPage}
        aaaVisaOffer={{
          title: 'AAA Visa Signature® credit card',
          description: 'Earn cash back on everyday purchases everywhere you go.',
          linkText: 'Learn more',
        }}
        createAccountHandler={handleCreateAccountClick}
        showAcquisitionBanner={{
          enabledBanners: [ProductSubType.Auto],
        }}
      />
    </div>
  );
};

export default Confirmation;
